import { useCallback, useEffect } from 'react';
import { getData, getId } from '../utils/item';
import { rectsIntersect } from '../components/board/boardCanvasSelection';
import { createShapeFromDraft } from '../components/board/boardDrawAdditions';
import { getCurveOffsetFromCursor } from '../components/board/boardDrawLinkGeometry';
import {
  toMapDrawingNodeId,
  toMapRegionNodeId,
  toMapShapeNodeId,
} from '../components/board/map/boardMapNodeIds.js';
import {
  getDragDelta,
  resizeLayout,
  translateLayout,
  translatePoints,
  updateDragHasMoved,
} from '../components/board/boardTransformDrag';

const EMPTY_REF = { current: null };
const EMPTY_MAP = new Map();
const NOOP = () => {};
const NO_TARGET_NODE_ID = () => null;
const MIN_MEASURE_POINT_DELTA = 2;
const EDGE_AUTOPAN_MARGIN = 56;
const EDGE_AUTOPAN_MAX_STEP = 18;

const sanitizeMeasurePoints = points =>
  Array.isArray(points)
    ? points.filter(
        point => Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)),
      )
    : [];

const buildMeasurePath = (points, offsetX = 0, offsetY = 0) => {
  const safePoints = sanitizeMeasurePoints(points);
  if (!safePoints.length) {
    return null;
  }
  const [firstPoint, ...restPoints] = safePoints;
  const commands = [`M ${Number(firstPoint.x) + offsetX} ${Number(firstPoint.y) + offsetY}`];
  restPoints.forEach(point => {
    commands.push(`L ${Number(point.x) + offsetX} ${Number(point.y) + offsetY}`);
  });
  if (restPoints.length === 0) {
    commands.push(`L ${Number(firstPoint.x) + offsetX} ${Number(firstPoint.y) + offsetY}`);
  }
  return commands.join(' ');
};

const computeMeasureLength = points => {
  const safePoints = sanitizeMeasurePoints(points);
  if (safePoints.length < 2) {
    return 0;
  }
  return safePoints.slice(1).reduce((sum, point, index) => {
    const previous = safePoints[index];
    return sum + Math.hypot(Number(point.x) - Number(previous.x), Number(point.y) - Number(previous.y));
  }, 0);
};

export const useBoardMapInteractions = params => {
  const {
    editable = false,
    containerRef,
    isAnyModalOpen,
    mapTool,
    measureMode = 'measure',
    mapShapeType,
    effectiveInteractionMode,
    viewport,
    setViewport,
    displayedPins,
    setDisplayedPins,
    displayedLinks,
    setDisplayedLinks,
    displayedShapes,
    setDisplayedShapes,
    displayedRegions,
    setDisplayedRegions,
    displayedDrawings,
    setDisplayedDrawings,
    displayedGroups,
    setDisplayedGroups,
    mapWidgets,
    widgetLayoutOverrides,
    setWidgetLayoutOverrides,
    mapState,
    persistMapField,
    persistMapWidgetLayout,
    clearSelectionBox,
    selectionBox,
    setSelectionBox,
    clearSelection,
    setSelectionState,
    selectPinOnly,
    selectLinkOnly,
    selectShapeOnly,
    selectRegionOnly,
    selectDrawingOnly,
    selectWidgetOnly,
    selectGroupOnly,
    pinDragStateRef,
    lineDraftRef,
    linkCurveDragStateRef,
    shapeDragStateRef,
    shapeResizeStateRef,
    drawingStateRef,
    shapeDraftRef,
    widgetDragStateRef,
    widgetResizeStateRef,
    groupDragStateRef,
    regionDragStateRef,
    regionPointDragStateRef,
    drawingDragStateRef,
    selectionDragStateRef,
    regionDrawStateRef,
    didPanRef,
    didPinDragRef,
    didShapeDragRef,
    didWidgetDragRef,
    didSelectionDragRef,
    didRegionDrawRef,
    didRegionPointDragRef,
    didDrawingRef,
    panStateRef,
    selectedPinIds,
    selectedShapeIds,
    selectedRegionIds,
    selectedDrawingIds,
    selectedWidgetIds,
    selectedGroupIds,
    mapNodeRectById,
    attachMapNodeToActiveGroup,
    setShapeDraft,
    setRegionDraftPoints,
    setRegionDraftHoverPoint,
    setDrawingDraftPoints,
    setLineDraft,
    getSnappedRegionPoint,
    isMapElementTarget,
    getMapTargetNodeId,
    onMeasureDraftComplete,
    renderMetrics,
  } = params;
  const safeLineDraftRef = lineDraftRef || EMPTY_REF;
  const safeMapNodeRectById = mapNodeRectById || EMPTY_MAP;
  const safeSetLineDraft = setLineDraft || NOOP;
  const safeSetDisplayedLinks = setDisplayedLinks || NOOP;
  const safeGetMapTargetNodeId = getMapTargetNodeId || NO_TARGET_NODE_ID;
  const safeSelectLinkOnly = selectLinkOnly || NOOP;

  const toWorldPoint = useCallback(
    (clientX, clientY, currentViewport = viewport) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        return null;
      }
      const offsetX = Number(renderMetrics?.offsetX) || 0;
      const offsetY = Number(renderMetrics?.offsetY) || 0;
      return {
        x: Math.round((clientX - rect.left - currentViewport.x) / currentViewport.zoom - offsetX),
        y: Math.round((clientY - rect.top - currentViewport.y) / currentViewport.zoom - offsetY),
      };
    },
    [containerRef, renderMetrics?.offsetX, renderMetrics?.offsetY, viewport],
  );

  const startMeasureDraft = useCallback(
    event => {
      if (mapTool !== 'measure' || event?.button !== 0 || !containerRef.current) {
        return false;
      }
      const worldPoint = toWorldPoint(event.clientX, event.clientY);
      if (!worldPoint) {
        return false;
      }
      const offsetX = Number(renderMetrics?.offsetX) || 0;
      const offsetY = Number(renderMetrics?.offsetY) || 0;
      const initial = {
        type: 'measure',
        viewport,
        fromX: worldPoint.x,
        fromY: worldPoint.y,
        toX: worldPoint.x,
        toY: worldPoint.y,
        points: [{ x: worldPoint.x, y: worldPoint.y }],
        path: buildMeasurePath([{ x: worldPoint.x, y: worldPoint.y }], offsetX, offsetY),
      };
      safeLineDraftRef.current = initial;
      safeSetLineDraft(initial);
      return true;
    },
    [
      containerRef,
      mapTool,
      renderMetrics?.offsetX,
      renderMetrics?.offsetY,
      safeLineDraftRef,
      safeSetLineDraft,
      toWorldPoint,
      viewport,
    ],
  );

  const buildLineDraftPath = useCallback(
    (fromNodeId, toX, toY) => {
      const safeFromRect = safeMapNodeRectById?.get(fromNodeId);
      if (!safeFromRect || !Number.isFinite(toX) || !Number.isFinite(toY)) {
        return null;
      }
      const offsetX = Number(renderMetrics?.offsetX) || 0;
      const offsetY = Number(renderMetrics?.offsetY) || 0;
      return `M ${safeFromRect.cx + offsetX} ${safeFromRect.cy + offsetY} L ${toX + offsetX} ${toY + offsetY}`;
    },
    [renderMetrics?.offsetX, renderMetrics?.offsetY, safeMapNodeRectById],
  );

  const getEdgeAutoPanDelta = useCallback(
    event => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (
        !rect ||
        !Number.isFinite(rect.width) ||
        !Number.isFinite(rect.height) ||
        rect.width <= 0 ||
        rect.height <= 0
      ) {
        return { x: 0, y: 0 };
      }
      const leftDistance = event.clientX - rect.left;
      const rightDistance = rect.right - event.clientX;
      const topDistance = event.clientY - rect.top;
      const bottomDistance = rect.bottom - event.clientY;
      const getAxisDelta = (startDistance, endDistance) => {
        if (startDistance < EDGE_AUTOPAN_MARGIN) {
          return Math.round(
            ((EDGE_AUTOPAN_MARGIN - startDistance) / EDGE_AUTOPAN_MARGIN) *
              EDGE_AUTOPAN_MAX_STEP,
          );
        }
        if (endDistance < EDGE_AUTOPAN_MARGIN) {
          return -Math.round(
            ((EDGE_AUTOPAN_MARGIN - endDistance) / EDGE_AUTOPAN_MARGIN) *
              EDGE_AUTOPAN_MAX_STEP,
          );
        }
        return 0;
      };
      return {
        x: getAxisDelta(leftDistance, rightDistance),
        y: getAxisDelta(topDistance, bottomDistance),
      };
    },
    [containerRef],
  );

  const applyElementDragAutoPan = useCallback(
    (event, dragState) => {
      const delta = getEdgeAutoPanDelta(event);
      if (!delta.x && !delta.y) {
        return;
      }
      dragState.autoPanX = (Number(dragState.autoPanX) || 0) + delta.x;
      dragState.autoPanY = (Number(dragState.autoPanY) || 0) + delta.y;
      setViewport(previous => ({
        ...previous,
        x: previous.x + delta.x,
        y: previous.y + delta.y,
      }));
    },
    [getEdgeAutoPanDelta, setViewport],
  );

  const getElementDragDelta = useCallback(
    (dragState, event) => {
      const { deltaWorldX, deltaWorldY } = getDragDelta(dragState, event, viewport.zoom);
      return {
        deltaWorldX: deltaWorldX - (Number(dragState.autoPanX) || 0) / viewport.zoom,
        deltaWorldY: deltaWorldY - (Number(dragState.autoPanY) || 0) / viewport.zoom,
      };
    },
    [viewport.zoom],
  );

  useEffect(() => {
    const handlePointerMove = event => {
      const lineDraft = safeLineDraftRef.current;
      if (lineDraft) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY, lineDraft.viewport);
        if (!worldPoint) {
          return;
        }
        if (lineDraft.type === 'measure') {
          const offsetX = Number(renderMetrics?.offsetX) || 0;
          const offsetY = Number(renderMetrics?.offsetY) || 0;
          const fromPoint = {
            x: Number(lineDraft?.fromX || 0),
            y: Number(lineDraft?.fromY || 0),
          };
          const currentPoints = sanitizeMeasurePoints(lineDraft?.points);
          const basePoints = currentPoints.length ? currentPoints : [fromPoint];
          const endPoint = { x: worldPoint.x, y: worldPoint.y };
          let nextPoints = basePoints;
          if (event.shiftKey) {
            nextPoints = [fromPoint, endPoint];
          } else {
            const lastPoint = basePoints[basePoints.length - 1];
            if (
              !lastPoint ||
              Math.hypot(endPoint.x - Number(lastPoint.x || 0), endPoint.y - Number(lastPoint.y || 0)) >=
                MIN_MEASURE_POINT_DELTA
            ) {
              nextPoints = [...basePoints, endPoint];
            }
          }
          const nextDraft = {
            ...lineDraft,
            toX: worldPoint.x,
            toY: worldPoint.y,
            points: nextPoints,
            path: buildMeasurePath(nextPoints, offsetX, offsetY),
          };
          safeLineDraftRef.current = nextDraft;
          safeSetLineDraft(nextDraft);
          return;
        }
        const nextDraft = {
          ...lineDraft,
          toX: worldPoint.x,
          toY: worldPoint.y,
          path: buildLineDraftPath(lineDraft.from, worldPoint.x, worldPoint.y),
        };
        safeLineDraftRef.current = nextDraft;
        safeSetLineDraft(nextDraft);
        return;
      }

      const linkCurveDragState = linkCurveDragStateRef?.current;
      if (linkCurveDragState) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const cursorWorldX =
          (event.clientX - rect.left - viewport.x) / viewport.zoom -
          (Number(renderMetrics?.offsetX) || 0);
        const cursorWorldY =
          (event.clientY - rect.top - viewport.y) / viewport.zoom -
          (Number(renderMetrics?.offsetY) || 0);
        const deltaX = event.clientX - linkCurveDragState.startClientX;
        const deltaY = event.clientY - linkCurveDragState.startClientY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          linkCurveDragState.hasMoved = true;
        }
        const nextLinks = displayedLinks.map(link => {
            if (link?.id !== linkCurveDragState.linkId) {
              return link;
            }
            const offset = getCurveOffsetFromCursor(
              link,
              safeMapNodeRectById,
              cursorWorldX,
              cursorWorldY,
            );
            if (!Number.isFinite(offset)) {
              return link;
            }
            return { ...link, curveOffset: offset };
          });
        linkCurveDragState.nextLinks = nextLinks;
        safeSetDisplayedLinks(nextLinks);
        return;
      }

      const pinDragState = pinDragStateRef.current;
      if (pinDragState) {
        updateDragHasMoved(pinDragState, event);
        applyElementDragAutoPan(event, pinDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(pinDragState, event);
        setDisplayedPins(prev =>
          prev.map(pin =>
            pinDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((pinDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((pinDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedShapes(prev =>
          prev.map(shape =>
            pinDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round((pinDragState.startShapesById?.[shape.id]?.x || 0) + deltaWorldX),
                  y: Math.round((pinDragState.startShapesById?.[shape.id]?.y || 0) + deltaWorldY),
                  points:
                    shape?.type === 'path' &&
                    Array.isArray(pinDragState.startShapesById?.[shape.id]?.points)
                      ? translatePoints(
                          pinDragState.startShapesById[shape.id].points,
                          deltaWorldX,
                          deltaWorldY,
                        )
                      : shape.points,
                }
              : shape,
          ),
        );
        setDisplayedRegions(prev =>
          prev.map(region =>
            pinDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: translatePoints(
                    pinDragState.startRegionsById?.[region.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : region,
          ),
        );
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            pinDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: translatePoints(
                    pinDragState.startDrawingsById?.[drawing.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : drawing,
          ),
        );
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          pinDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              pinDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        return;
      }

      const shapeDragState = shapeDragStateRef.current;
      if (shapeDragState) {
        updateDragHasMoved(shapeDragState, event);
        applyElementDragAutoPan(event, shapeDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(shapeDragState, event);
        setDisplayedShapes(prev =>
          prev.map(shape =>
            shapeDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round(
                    (
                      shapeDragState.startShapesById?.[shape.id]?.x ??
                      (Number(shape?.x) || 0)
                    ) +
                      deltaWorldX,
                  ),
                  y: Math.round(
                    (
                      shapeDragState.startShapesById?.[shape.id]?.y ??
                      (Number(shape?.y) || 0)
                    ) +
                      deltaWorldY,
                  ),
                }
              : shape,
          ),
        );
        setDisplayedPins(prev =>
          prev.map(pin =>
            shapeDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((shapeDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((shapeDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedRegions(prev =>
          prev.map(region =>
            shapeDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: translatePoints(
                    shapeDragState.startRegionsById?.[region.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : region,
          ),
        );
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            shapeDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: translatePoints(
                    shapeDragState.startDrawingsById?.[drawing.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : drawing,
          ),
        );
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          shapeDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              shapeDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        return;
      }

      const shapeResizeState = shapeResizeStateRef.current;
      if (shapeResizeState) {
        updateDragHasMoved(shapeResizeState, event);
        const { deltaWorldX, deltaWorldY } = getDragDelta(
          shapeResizeState,
          event,
          viewport.zoom,
        );
        setDisplayedShapes(prev =>
          prev.map(shape =>
            shape.id === shapeResizeState.shapeId
              ? {
                  ...shape,
                  w: Math.max(8, Math.round(shapeResizeState.startW + deltaWorldX)),
                  h: Math.max(8, Math.round(shapeResizeState.startH + deltaWorldY)),
                }
              : shape,
          ),
        );
        return;
      }

      const widgetDragState = widgetDragStateRef.current;
      if (widgetDragState) {
        updateDragHasMoved(widgetDragState, event);
        applyElementDragAutoPan(event, widgetDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(widgetDragState, event);
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          widgetDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              widgetDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        setDisplayedShapes(prev =>
          prev.map(shape =>
            widgetDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round((widgetDragState.startShapesById?.[shape.id]?.x || 0) + deltaWorldX),
                  y: Math.round((widgetDragState.startShapesById?.[shape.id]?.y || 0) + deltaWorldY),
                  points:
                    shape?.type === 'path' &&
                    Array.isArray(widgetDragState.startShapesById?.[shape.id]?.points)
                      ? translatePoints(
                          widgetDragState.startShapesById[shape.id].points,
                          deltaWorldX,
                          deltaWorldY,
                        )
                      : shape.points,
                }
              : shape,
          ),
        );
        setDisplayedPins(prev =>
          prev.map(pin =>
            widgetDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((widgetDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((widgetDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedRegions(prev =>
          prev.map(region =>
            widgetDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: translatePoints(
                    widgetDragState.startRegionsById?.[region.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : region,
          ),
        );
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            widgetDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: translatePoints(
                    widgetDragState.startDrawingsById?.[drawing.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : drawing,
          ),
        );
        return;
      }

      const groupDragState = groupDragStateRef.current;
      if (groupDragState) {
        updateDragHasMoved(groupDragState, event);
        applyElementDragAutoPan(event, groupDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(groupDragState, event);
        setDisplayedGroups(prev =>
          prev.map(group =>
            group.id === groupDragState.groupId
              ? {
                  ...group,
                  x: Math.round(groupDragState.startGroup.x + deltaWorldX),
                  y: Math.round(groupDragState.startGroup.y + deltaWorldY),
                }
              : group,
          ),
        );
        setDisplayedPins(prev =>
          prev.map(pin =>
            groupDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((groupDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((groupDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedShapes(prev =>
          prev.map(shape =>
            groupDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round((groupDragState.startShapesById?.[shape.id]?.x || 0) + deltaWorldX),
                  y: Math.round((groupDragState.startShapesById?.[shape.id]?.y || 0) + deltaWorldY),
                  points:
                    shape?.type === 'path' &&
                    Array.isArray(groupDragState.startShapesById?.[shape.id]?.points)
                      ? translatePoints(
                          groupDragState.startShapesById[shape.id].points,
                          deltaWorldX,
                          deltaWorldY,
                        )
                      : shape.points,
                }
              : shape,
          ),
        );
        setDisplayedRegions(prev =>
          prev.map(region =>
            groupDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: translatePoints(
                    groupDragState.startRegionsById?.[region.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : region,
          ),
        );
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            groupDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: translatePoints(
                    groupDragState.startDrawingsById?.[drawing.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : drawing,
          ),
        );
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          groupDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              groupDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        return;
      }

      const widgetResizeState = widgetResizeStateRef.current;
      if (widgetResizeState) {
        updateDragHasMoved(widgetResizeState, event);
        const { deltaWorldX, deltaWorldY } = getDragDelta(
          widgetResizeState,
          event,
          viewport.zoom,
        );
        setWidgetLayoutOverrides(prev => ({
          ...prev,
          [widgetResizeState.widgetId]: resizeLayout(
            widgetResizeState.startLayout,
            deltaWorldX,
            deltaWorldY,
            { w: 80, h: 24 },
          ),
        }));
        return;
      }

      const regionDragState = regionDragStateRef.current;
      if (regionDragState) {
        updateDragHasMoved(regionDragState, event);
        applyElementDragAutoPan(event, regionDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(regionDragState, event);
        setDisplayedRegions(prev =>
          prev.map(region =>
            regionDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: Array.isArray(regionDragState.startRegionsById?.[region.id]?.points)
                    ? translatePoints(
                        regionDragState.startRegionsById[region.id].points,
                        deltaWorldX,
                        deltaWorldY,
                      )
                    : region.points,
                }
              : region,
          ),
        );
        setDisplayedShapes(prev =>
          prev.map(shape =>
            regionDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round((regionDragState.startShapesById?.[shape.id]?.x || 0) + deltaWorldX),
                  y: Math.round((regionDragState.startShapesById?.[shape.id]?.y || 0) + deltaWorldY),
                  points:
                    shape?.type === 'path' &&
                    Array.isArray(regionDragState.startShapesById?.[shape.id]?.points)
                      ? translatePoints(
                          regionDragState.startShapesById[shape.id].points,
                          deltaWorldX,
                          deltaWorldY,
                        )
                      : shape.points,
                }
              : shape,
          ),
        );
        setDisplayedPins(prev =>
          prev.map(pin =>
            regionDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((regionDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((regionDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            regionDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: translatePoints(
                    regionDragState.startDrawingsById?.[drawing.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : drawing,
          ),
        );
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          regionDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              regionDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        return;
      }

      const regionPointDragState = regionPointDragStateRef.current;
      if (regionPointDragState) {
        const worldPoint = toWorldPoint(
          event.clientX,
          event.clientY,
          regionPointDragState.viewport,
        );
        if (!worldPoint) {
          return;
        }
        const snappedPoint = getSnappedRegionPoint({
          point: worldPoint,
          regions: displayedRegions,
          zoom: regionPointDragState.viewport.zoom,
          excludeRegionId: regionPointDragState.regionId,
          excludePointIndex: regionPointDragState.pointIndex,
        });
        const deltaX = event.clientX - regionPointDragState.startClientX;
        const deltaY = event.clientY - regionPointDragState.startClientY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          regionPointDragState.hasMoved = true;
        }
        setDisplayedRegions(prev =>
          prev.map(region =>
            region.id === regionPointDragState.regionId
              ? {
                  ...region,
                  points: Array.isArray(region.points)
                    ? region.points.map((point, index) =>
                        index === regionPointDragState.pointIndex
                          ? { ...point, x: snappedPoint.x, y: snappedPoint.y }
                          : point,
                      )
                    : region.points,
                }
              : region,
          ),
        );
        return;
      }

      const drawingDragState = drawingDragStateRef.current;
      if (drawingDragState) {
        updateDragHasMoved(drawingDragState, event);
        applyElementDragAutoPan(event, drawingDragState);
        const { deltaWorldX, deltaWorldY } = getElementDragDelta(drawingDragState, event);
        setDisplayedDrawings(prev =>
          prev.map(drawing =>
            drawingDragState.drawingIds.includes(drawing.id)
              ? {
                  ...drawing,
                  points: Array.isArray(drawingDragState.startDrawingsById?.[drawing.id]?.points)
                    ? translatePoints(
                        drawingDragState.startDrawingsById[drawing.id].points,
                        deltaWorldX,
                        deltaWorldY,
                      )
                    : drawing.points,
                }
              : drawing,
          ),
        );
        setDisplayedShapes(prev =>
          prev.map(shape =>
            drawingDragState.shapeIds.includes(shape.id)
              ? {
                  ...shape,
                  x: Math.round((drawingDragState.startShapesById?.[shape.id]?.x || 0) + deltaWorldX),
                  y: Math.round((drawingDragState.startShapesById?.[shape.id]?.y || 0) + deltaWorldY),
                  points:
                    shape?.type === 'path' &&
                    Array.isArray(drawingDragState.startShapesById?.[shape.id]?.points)
                      ? translatePoints(
                          drawingDragState.startShapesById[shape.id].points,
                          deltaWorldX,
                          deltaWorldY,
                        )
                      : shape.points,
                }
              : shape,
          ),
        );
        setDisplayedPins(prev =>
          prev.map(pin =>
            drawingDragState.pinIds.includes(pin.id)
              ? {
                  ...pin,
                  x: Math.round((drawingDragState.startPinsById?.[pin.id]?.x || 0) + deltaWorldX),
                  y: Math.round((drawingDragState.startPinsById?.[pin.id]?.y || 0) + deltaWorldY),
                }
              : pin,
          ),
        );
        setDisplayedRegions(prev =>
          prev.map(region =>
            drawingDragState.regionIds.includes(region.id)
              ? {
                  ...region,
                  points: translatePoints(
                    drawingDragState.startRegionsById?.[region.id]?.points || [],
                    deltaWorldX,
                    deltaWorldY,
                  ),
                }
              : region,
          ),
        );
        setWidgetLayoutOverrides(prev => {
          const next = { ...prev };
          drawingDragState.widgetIds.forEach(widgetId => {
            next[widgetId] = translateLayout(
              drawingDragState.startLayoutsById?.[widgetId],
              deltaWorldX,
              deltaWorldY,
              { w: 80, h: 24 },
            );
          });
          return next;
        });
        return;
      }

      const drawingState = drawingStateRef.current;
      if (drawingState) {
        const worldPoint = toWorldPoint(
          event.clientX,
          event.clientY,
          drawingState.viewport,
        );
        if (!worldPoint) {
          return;
        }
        const deltaX = event.clientX - drawingState.startClientX;
        const deltaY = event.clientY - drawingState.startClientY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          drawingState.hasMoved = true;
        }
        const lastPoint = drawingState.points[drawingState.points.length - 1];
        const minDistance = Math.max(2, Math.round(4 / drawingState.viewport.zoom));
        if (
          !lastPoint ||
          Math.hypot(worldPoint.x - lastPoint.x, worldPoint.y - lastPoint.y) >=
            minDistance
        ) {
          const nextPoints = [...drawingState.points, worldPoint];
          drawingState.points = nextPoints;
          setDrawingDraftPoints(nextPoints);
        }
        return;
      }

      const nextShapeDraft = shapeDraftRef.current;
      if (nextShapeDraft) {
        const worldPoint = toWorldPoint(
          event.clientX,
          event.clientY,
          nextShapeDraft.viewport,
        );
        if (!worldPoint) {
          return;
        }
        const dx = worldPoint.x - nextShapeDraft.startX;
        const dy = worldPoint.y - nextShapeDraft.startY;
        const nextW = Math.max(Math.abs(dx), 8);
        const nextH = Math.max(Math.abs(dy), 8);
        const draft = {
          ...nextShapeDraft,
          x: dx >= 0 ? nextShapeDraft.startX : nextShapeDraft.startX - nextW,
          y: dy >= 0 ? nextShapeDraft.startY : nextShapeDraft.startY - nextH,
          w: nextW,
          h: nextH,
        };
        shapeDraftRef.current = draft;
        setShapeDraft(draft);
        return;
      }

      const selectionDragState = selectionDragStateRef.current;
      if (selectionDragState) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) {
          return;
        }
        const localX = event.clientX - rect.left;
        const localY = event.clientY - rect.top;
        const deltaX = localX - selectionDragState.startX;
        const deltaY = localY - selectionDragState.startY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          selectionDragState.hasMoved = true;
        }
        setSelectionBox({
          x: Math.min(selectionDragState.startX, localX),
          y: Math.min(selectionDragState.startY, localY),
          w: Math.abs(localX - selectionDragState.startX),
          h: Math.abs(localY - selectionDragState.startY),
        });
        return;
      }

      const regionDrawState = regionDrawStateRef.current;
      if (regionDrawState && containerRef.current) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY);
        if (!worldPoint) {
          return;
        }
        const snappedPoint = getSnappedRegionPoint({
          point: worldPoint,
          regions: displayedRegions,
          zoom: viewport.zoom,
        });
        const lastPoint = regionDrawState.points[regionDrawState.points.length - 1];
        const minDistance = Math.max(4, Math.round(8 / viewport.zoom));
        if (
          !lastPoint ||
          Math.hypot(snappedPoint.x - lastPoint.x, snappedPoint.y - lastPoint.y) >=
            minDistance
        ) {
          const nextPoints = [...regionDrawState.points, snappedPoint];
          regionDrawState.points = nextPoints;
          setRegionDraftPoints(nextPoints);
        }
        setRegionDraftHoverPoint(snappedPoint);
        return;
      }

      if (mapTool === 'region' && containerRef.current) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY);
        if (!worldPoint) {
          return;
        }
        setRegionDraftHoverPoint(
          getSnappedRegionPoint({
            point: worldPoint,
            regions: displayedRegions,
            zoom: viewport.zoom,
          }),
        );
      }

      const panState = panStateRef.current;
      if (!panState) {
        return;
      }
      const deltaX = event.clientX - panState.startClientX;
      const deltaY = event.clientY - panState.startClientY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        panState.hasMoved = true;
      }
      setViewport({
        ...panState.startViewport,
        x: panState.startViewport.x + deltaX,
        y: panState.startViewport.y + deltaY,
      });
    };

    const handlePointerUp = async event => {
      const lineDraft = safeLineDraftRef.current;
      if (lineDraft) {
        safeLineDraftRef.current = null;
        safeSetLineDraft(null);
        if (lineDraft.type === 'measure') {
          const startPoint = {
            x: Number(lineDraft?.fromX || 0),
            y: Number(lineDraft?.fromY || 0),
          };
          const endPoint = {
            x: Number(lineDraft?.toX || 0),
            y: Number(lineDraft?.toY || 0),
          };
          const safePoints = sanitizeMeasurePoints(lineDraft?.points);
          const finalizedPoints =
            safePoints.length >= 2 ? safePoints : [startPoint, endPoint];
          const length = computeMeasureLength(finalizedPoints);
          if (length >= 6) {
            await onMeasureDraftComplete?.({
              x1: Number(finalizedPoints[0]?.x || 0),
              y1: Number(finalizedPoints[0]?.y || 0),
              x2: Number(finalizedPoints[finalizedPoints.length - 1]?.x || 0),
              y2: Number(finalizedPoints[finalizedPoints.length - 1]?.y || 0),
              points: finalizedPoints,
              length,
              settingsMode: measureMode === 'settings',
            });
          }
          return;
        }
        const targetElement =
          document.elementFromPoint(event.clientX, event.clientY) || event.target;
        const targetNodeId = safeGetMapTargetNodeId(targetElement);
        if (
          targetNodeId &&
          targetNodeId !== lineDraft.from &&
          safeMapNodeRectById?.has(targetNodeId)
        ) {
          const nextLink = {
            id:
              typeof crypto !== 'undefined' &&
              typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `link_${Date.now()}`,
            from: lineDraft.from,
            to: targetNodeId,
            color: 'secondary',
            arrows: 'none',
            curveOffset: 0,
          };
          const nextLinks = [...displayedLinks, nextLink];
          safeSetDisplayedLinks(nextLinks);
          await persistMapField('links', nextLinks);
          safeSelectLinkOnly(nextLink.id);
        }
        return;
      }
      const activeLinkCurveDragState = linkCurveDragStateRef?.current;
      if (activeLinkCurveDragState) {
        linkCurveDragStateRef.current = null;
        if (activeLinkCurveDragState.hasMoved) {
          await persistMapField(
            'links',
            activeLinkCurveDragState.nextLinks || displayedLinks,
          );
        }
        return;
      }
      const pinDragState = pinDragStateRef.current;
      if (pinDragState) {
        if (pinDragState.hasMoved) {
          didPinDragRef.current = pinDragState.pinIds?.[0] || null;
        }
        pinDragStateRef.current = null;
        if (pinDragState.hasMoved) {
          await Promise.all([
            persistMapField('pins', displayedPins),
            pinDragState.shapeIds?.length
              ? persistMapField('shapes', displayedShapes)
              : Promise.resolve(),
            pinDragState.regionIds?.length
              ? persistMapField('regions', displayedRegions)
              : Promise.resolve(),
            pinDragState.drawingIds?.length
              ? persistMapField('drawings', displayedDrawings)
              : Promise.resolve(),
            ...(pinDragState.widgetIds || []).map(widgetId =>
              persistMapWidgetLayout(
                widgetId,
                widgetLayoutOverrides[widgetId] ||
                  pinDragState.startLayoutsById?.[widgetId],
              ),
            ),
          ]);
        }
        return;
      }
      const shapeDragState = shapeDragStateRef.current;
      if (shapeDragState) {
        if (shapeDragState.hasMoved && didShapeDragRef) {
          didShapeDragRef.current = shapeDragState.id;
        }
        shapeDragStateRef.current = null;
        if (shapeDragState.hasMoved) {
          await Promise.all([
            persistMapField('shapes', displayedShapes),
            shapeDragState.pinIds?.length
              ? persistMapField('pins', displayedPins)
              : Promise.resolve(),
            shapeDragState.regionIds?.length
              ? persistMapField('regions', displayedRegions)
              : Promise.resolve(),
            shapeDragState.drawingIds?.length
              ? persistMapField('drawings', displayedDrawings)
              : Promise.resolve(),
            ...(shapeDragState.widgetIds || []).map(widgetId =>
              persistMapWidgetLayout(
                widgetId,
                widgetLayoutOverrides[widgetId] ||
                  shapeDragState.startLayoutsById?.[widgetId],
              ),
            ),
          ]);
        }
        return;
      }
      const shapeResizeState = shapeResizeStateRef.current;
      if (shapeResizeState) {
        if (shapeResizeState.hasMoved && didShapeDragRef) {
          didShapeDragRef.current = shapeResizeState.id;
        }
        shapeResizeStateRef.current = null;
        if (shapeResizeState.hasMoved) {
          await persistMapField('shapes', displayedShapes);
        }
        return;
      }
      const regionPointDragState = regionPointDragStateRef.current;
      if (regionPointDragState) {
        if (regionPointDragState.hasMoved) {
          didRegionPointDragRef.current = true;
        }
        regionPointDragStateRef.current = null;
        await persistMapField('regions', displayedRegions);
        return;
      }
      const regionDragState = regionDragStateRef.current;
      if (regionDragState) {
        regionDragStateRef.current = null;
        if (regionDragState.hasMoved) {
          await Promise.all([
            persistMapField('regions', displayedRegions),
            regionDragState.pinIds?.length
              ? persistMapField('pins', displayedPins)
              : Promise.resolve(),
            regionDragState.shapeIds?.length
              ? persistMapField('shapes', displayedShapes)
              : Promise.resolve(),
            regionDragState.drawingIds?.length
              ? persistMapField('drawings', displayedDrawings)
              : Promise.resolve(),
            ...(regionDragState.widgetIds || []).map(widgetId =>
              persistMapWidgetLayout(
                widgetId,
                widgetLayoutOverrides[widgetId] ||
                  regionDragState.startLayoutsById?.[widgetId],
              ),
            ),
          ]);
        }
        return;
      }
      const widgetDragState = widgetDragStateRef.current;
      if (widgetDragState) {
        if (widgetDragState.hasMoved && didWidgetDragRef) {
          didWidgetDragRef.current = [...(widgetDragState.widgetIds || [])];
        }
        widgetDragStateRef.current = null;
        if (widgetDragState.hasMoved) {
          await Promise.all(
            [
              ...widgetDragState.widgetIds.map(widgetId =>
                persistMapWidgetLayout(
                  widgetId,
                  widgetLayoutOverrides[widgetId] ||
                    widgetDragState.startLayoutsById?.[widgetId],
                ),
              ),
              widgetDragState.pinIds?.length
                ? persistMapField('pins', displayedPins)
                : Promise.resolve(),
              widgetDragState.shapeIds?.length
                ? persistMapField('shapes', displayedShapes)
                : Promise.resolve(),
              widgetDragState.regionIds?.length
                ? persistMapField('regions', displayedRegions)
                : Promise.resolve(),
              widgetDragState.drawingIds?.length
                ? persistMapField('drawings', displayedDrawings)
                : Promise.resolve(),
            ],
          );
        }
        return;
      }
      const widgetResizeState = widgetResizeStateRef.current;
      if (widgetResizeState) {
        if (widgetResizeState.hasMoved && didWidgetDragRef) {
          didWidgetDragRef.current = [widgetResizeState.widgetId];
        }
        widgetResizeStateRef.current = null;
        if (widgetResizeState.hasMoved) {
          await persistMapWidgetLayout(
            widgetResizeState.widgetId,
            widgetLayoutOverrides[widgetResizeState.widgetId] || widgetResizeState.startLayout,
          );
        }
        return;
      }
      const activeGroupDragState = groupDragStateRef.current;
      if (activeGroupDragState) {
        groupDragStateRef.current = null;
        if (activeGroupDragState.hasMoved) {
          await Promise.all([
            persistMapField('groups', displayedGroups),
            persistMapField('pins', displayedPins),
            persistMapField('shapes', displayedShapes),
            persistMapField('regions', displayedRegions),
            persistMapField('drawings', displayedDrawings),
            ...activeGroupDragState.widgetIds.map(widgetId =>
              persistMapWidgetLayout(
                widgetId,
                widgetLayoutOverrides[widgetId] ||
                  activeGroupDragState.startLayoutsById?.[widgetId],
              ),
            ),
          ]);
        }
        return;
      }
      const drawingState = drawingStateRef.current;
      if (drawingState) {
        drawingStateRef.current = null;
        if (drawingState.hasMoved) {
          didDrawingRef.current = true;
        }
        const finalizedPoints = drawingState.points || [];
        if (finalizedPoints.length >= 2 && drawingState.hasMoved) {
          const nextDrawing = {
            id:
              typeof crypto !== 'undefined' &&
              typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `drawing_${Date.now()}`,
            type: 'path',
            points: finalizedPoints,
            stroke: 'secondary',
            strokeWidth: 2,
            strokeStyle: 'solid',
            strokeDasharray: null,
            fill: null,
            fillOpacity: 0,
            visible: true,
            locked: false,
          };
          const nextDrawings = [...mapState.drawings, nextDrawing];
          setDisplayedDrawings(nextDrawings);
          const xs = finalizedPoints.map(point => Number(point?.x) || 0);
          const ys = finalizedPoints.map(point => Number(point?.y) || 0);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);
          const nextGroups = await attachMapNodeToActiveGroup({
            nodeId: toMapDrawingNodeId(nextDrawing.id),
            rect: {
              x: minX,
              y: minY,
              w: Math.max(1, maxX - minX),
              h: Math.max(1, maxY - minY),
            },
            persist: false,
          });
          await Promise.all([
            persistMapField('drawings', nextDrawings),
            nextGroups !== displayedGroups
              ? persistMapField('groups', nextGroups)
              : Promise.resolve(),
          ]);
        }
        setDrawingDraftPoints([]);
        return;
      }
      const drawingDragState = drawingDragStateRef.current;
      if (drawingDragState) {
        drawingDragStateRef.current = null;
        if (drawingDragState.hasMoved) {
          await Promise.all([
            persistMapField('drawings', displayedDrawings),
            drawingDragState.pinIds?.length
              ? persistMapField('pins', displayedPins)
              : Promise.resolve(),
            drawingDragState.shapeIds?.length
              ? persistMapField('shapes', displayedShapes)
              : Promise.resolve(),
            drawingDragState.regionIds?.length
              ? persistMapField('regions', displayedRegions)
              : Promise.resolve(),
            ...(drawingDragState.widgetIds || []).map(widgetId =>
              persistMapWidgetLayout(
                widgetId,
                widgetLayoutOverrides[widgetId] ||
                  drawingDragState.startLayoutsById?.[widgetId],
              ),
            ),
          ]);
        }
        return;
      }
      const nextShapeDraft = shapeDraftRef.current;
      if (nextShapeDraft) {
        shapeDraftRef.current = null;
        setShapeDraft(null);
        if (
          Number.isFinite(nextShapeDraft?.w) &&
          Number.isFinite(nextShapeDraft?.h) &&
          nextShapeDraft.w >= 8 &&
          nextShapeDraft.h >= 8
        ) {
          const nextShape = createShapeFromDraft(nextShapeDraft);
          if (!nextShape) {
            return;
          }
          const nextShapes = [...mapState.shapes, nextShape];
          setDisplayedShapes(nextShapes);
          const nextGroups = await attachMapNodeToActiveGroup({
            nodeId: toMapShapeNodeId(nextShape.id),
            rect: {
              x: Number(nextShape?.x) || 0,
              y: Number(nextShape?.y) || 0,
              w: Math.max(8, Number(nextShape?.w) || 0),
              h: Math.max(8, Number(nextShape?.h) || 0),
            },
            persist: false,
          });
          await Promise.all([
            persistMapField('shapes', nextShapes),
            nextGroups !== displayedGroups
              ? persistMapField('groups', nextGroups)
              : Promise.resolve(),
          ]);
          selectShapeOnly(nextShape.id);
        }
        return;
      }
      const selectionDragState = selectionDragStateRef.current;
      if (selectionDragState) {
        if (selectionDragState.hasMoved) {
          didSelectionDragRef.current = true;
          const rect = selectionBox || {
            x: selectionDragState.startX,
            y: selectionDragState.startY,
            w: 0,
            h: 0,
          };
          const minWorldX =
            (rect.x - viewport.x) / viewport.zoom - (Number(renderMetrics?.offsetX) || 0);
          const minWorldY =
            (rect.y - viewport.y) / viewport.zoom - (Number(renderMetrics?.offsetY) || 0);
          const maxWorldX =
            (rect.x + rect.w - viewport.x) / viewport.zoom -
            (Number(renderMetrics?.offsetX) || 0);
          const maxWorldY =
            (rect.y + rect.h - viewport.y) / viewport.zoom -
            (Number(renderMetrics?.offsetY) || 0);
          const normalizedWorldRect = {
            x: minWorldX,
            y: minWorldY,
            w: maxWorldX - minWorldX,
            h: maxWorldY - minWorldY,
          };
          const nextSelectedPinIds = displayedPins
            .filter(pin => {
              const pinX = Number(pin?.x) || 0;
              const pinY = Number(pin?.y) || 0;
              return (
                pinX >= minWorldX &&
                pinX <= maxWorldX &&
                pinY >= minWorldY &&
                pinY <= maxWorldY
              );
            })
            .map(pin => pin.id);
          const nextSelectedWidgetIds = mapWidgets
            .filter(widget => {
              const elementId = getId(widget);
              const payload = getData(widget) || {};
              const layout = widgetLayoutOverrides[elementId] || payload?.map || {};
              return rectsIntersect(normalizedWorldRect, {
                x: Number(layout?.x) || 0,
                y: Number(layout?.y) || 0,
                w: Math.max(80, Number(layout?.w) || 0),
                h: Math.max(24, Number(layout?.h) || 0),
              });
            })
            .map(widget => getId(widget))
            .filter(Boolean);
          const nextSelectedShapeIds = displayedShapes
            .filter(shape =>
              rectsIntersect(normalizedWorldRect, {
                x: Number(shape?.x) || 0,
                y: Number(shape?.y) || 0,
                w: Math.max(8, Number(shape?.w) || 0),
                h: Math.max(8, Number(shape?.h) || 0),
              }),
            )
            .map(shape => shape.id)
            .filter(Boolean);
          const nextSelectedRegionIds = displayedRegions
            .filter(region => {
              const points = Array.isArray(region?.points) ? region.points : [];
              if (!points.length) {
                return false;
              }
              const xs = points.map(point => Number(point?.x) || 0);
              const ys = points.map(point => Number(point?.y) || 0);
              return rectsIntersect(normalizedWorldRect, {
                x: Math.min(...xs),
                y: Math.min(...ys),
                w: Math.max(...xs) - Math.min(...xs),
                h: Math.max(...ys) - Math.min(...ys),
              });
            })
            .map(region => region.id)
            .filter(Boolean);
          const nextSelectedDrawingIds = displayedDrawings
            .filter(drawing => {
              const points = Array.isArray(drawing?.points) ? drawing.points : [];
              if (points.length < 2) {
                return false;
              }
              const xs = points.map(point => Number(point?.x) || 0);
              const ys = points.map(point => Number(point?.y) || 0);
              const drawingMinX = Math.min(...xs);
              const drawingMinY = Math.min(...ys);
              const drawingMaxX = Math.max(...xs);
              const drawingMaxY = Math.max(...ys);
              return !(
                drawingMaxX < minWorldX ||
                drawingMinX > maxWorldX ||
                drawingMaxY < minWorldY ||
                drawingMinY > maxWorldY
              );
            })
            .map(drawing => drawing.id);
          setSelectionState({
            selectedPinIds: nextSelectedPinIds,
            selectedPinId: nextSelectedPinIds.length === 1 ? nextSelectedPinIds[0] : null,
            selectedShapeIds: nextSelectedShapeIds,
            selectedShapeId:
              nextSelectedShapeIds.length === 1 ? nextSelectedShapeIds[0] : null,
            selectedRegionIds: nextSelectedRegionIds,
            selectedRegionId:
              nextSelectedRegionIds.length === 1 ? nextSelectedRegionIds[0] : null,
            selectedDrawingIds: nextSelectedDrawingIds,
            selectedDrawingId:
              nextSelectedDrawingIds.length === 1 ? nextSelectedDrawingIds[0] : null,
            selectedWidgetIds: nextSelectedWidgetIds,
            selectedWidgetId:
              nextSelectedWidgetIds.length === 1 ? nextSelectedWidgetIds[0] : null,
          });
        }
        selectionDragStateRef.current = null;
        clearSelectionBox();
        return;
      }
      const regionDrawState = regionDrawStateRef.current;
      if (regionDrawState) {
        regionDrawStateRef.current = null;
        didRegionDrawRef.current = true;
        const finalizedPoints = regionDrawState.points || [];
        if (finalizedPoints.length >= 3) {
          const nextRegion = {
            id:
              typeof crypto !== 'undefined' &&
              typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `region_${Date.now()}`,
            label: '',
            points: finalizedPoints,
            fill: 'secondary',
            fillOpacity: 0.2,
            stroke: 'secondary',
            strokeWidth: 2,
            linkedItemId: null,
            visible: true,
            locked: false,
          };
          const nextRegions = [...mapState.regions, nextRegion];
          const xs = finalizedPoints.map(point => Number(point?.x) || 0);
          const ys = finalizedPoints.map(point => Number(point?.y) || 0);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);
          const nextGroups = await attachMapNodeToActiveGroup({
            nodeId: toMapRegionNodeId(nextRegion.id),
            rect: {
              x: minX,
              y: minY,
              w: Math.max(1, maxX - minX),
              h: Math.max(1, maxY - minY),
            },
            persist: false,
          });
          await Promise.all([
            persistMapField('regions', nextRegions),
            nextGroups !== displayedGroups
              ? persistMapField('groups', nextGroups)
              : Promise.resolve(),
          ]);
          selectRegionOnly(nextRegion.id);
        }
        setRegionDraftPoints([]);
        setRegionDraftHoverPoint(null);
        return;
      }
      if (panStateRef.current?.hasMoved) {
        didPanRef.current = true;
      }
      panStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [
    applyElementDragAutoPan,
    buildLineDraftPath,
    clearSelectionBox,
    displayedDrawings,
    displayedGroups,
    displayedLinks,
    displayedPins,
    displayedRegions,
    displayedShapes,
    mapState.drawings,
    mapState.links,
    mapState.regions,
    mapState.shapes,
    mapNodeRectById,
    measureMode,
    mapTool,
    mapWidgets,
    attachMapNodeToActiveGroup,
    persistMapField,
    persistMapWidgetLayout,
    selectionBox,
    selectLinkOnly,
    selectRegionOnly,
    selectShapeOnly,
    setDisplayedDrawings,
    setDisplayedGroups,
    setDisplayedLinks,
    setDisplayedPins,
    setDisplayedRegions,
    setDisplayedShapes,
    setDrawingDraftPoints,
    setLineDraft,
    setRegionDraftHoverPoint,
    setRegionDraftPoints,
    setSelectionBox,
    setSelectionState,
    setShapeDraft,
    setViewport,
    setWidgetLayoutOverrides,
    onMeasureDraftComplete,
    viewport.x,
    viewport.y,
    viewport.zoom,
    widgetLayoutOverrides,
    containerRef,
    didDrawingRef,
    didPanRef,
    didPinDragRef,
    didShapeDragRef,
    didWidgetDragRef,
    didRegionDrawRef,
    didRegionPointDragRef,
    didSelectionDragRef,
    drawingDragStateRef,
    drawingStateRef,
    getMapTargetNodeId,
    getElementDragDelta,
    getSnappedRegionPoint,
    groupDragStateRef,
    linkCurveDragStateRef,
    lineDraftRef,
    panStateRef,
    pinDragStateRef,
    regionDragStateRef,
    regionDrawStateRef,
    regionPointDragStateRef,
    renderMetrics?.offsetX,
    renderMetrics?.offsetY,
    safeGetMapTargetNodeId,
    safeLineDraftRef,
    safeMapNodeRectById,
    safeSelectLinkOnly,
    safeSetDisplayedLinks,
    safeSetLineDraft,
    selectionDragStateRef,
    shapeDragStateRef,
    shapeDraftRef,
    shapeResizeStateRef,
    toWorldPoint,
    widgetDragStateRef,
    widgetResizeStateRef,
  ]);

  const handleCanvasPointerDown = useCallback(
    event => {
      if (isAnyModalOpen) {
        return;
      }
      if (!editable) {
        if (startMeasureDraft(event)) {
          return;
        }
        if (effectiveInteractionMode === 'pan' && event.button === 0) {
          panStateRef.current = {
            startClientX: event.clientX,
            startClientY: event.clientY,
            startViewport: viewport,
            hasMoved: false,
          };
        }
        return;
      }
      if (effectiveInteractionMode === 'pan' && event.button === 0) {
        panStateRef.current = {
          startClientX: event.clientX,
          startClientY: event.clientY,
          startViewport: viewport,
          hasMoved: false,
        };
        return;
      }
      if (isMapElementTarget(event.target)) {
        if (mapTool === 'line' && event.button === 0) {
          const nodeId = safeGetMapTargetNodeId(event.target);
          const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
          if (nodeId && fromRect) {
            const initial = {
              from: nodeId,
              viewport,
              toX: fromRect.cx,
              toY: fromRect.cy,
              path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
            };
            safeLineDraftRef.current = initial;
            safeSetLineDraft(initial);
          }
        }
        if (startMeasureDraft(event)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (startMeasureDraft(event)) {
        return;
      }
      if (mapTool === 'shape' && event.button === 0 && containerRef.current) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY);
        if (!worldPoint) {
          return;
        }
        const initial = {
          type: ['rect', 'diamond', 'triangle', 'star'].includes(mapShapeType)
            ? mapShapeType
            : 'circle',
          startX: worldPoint.x,
          startY: worldPoint.y,
          x: worldPoint.x,
          y: worldPoint.y,
          w: 0,
          h: 0,
          viewport,
        };
        shapeDraftRef.current = initial;
        setShapeDraft(initial);
        return;
      }
      if (mapTool === 'region' && event.button === 0 && containerRef.current) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY);
        if (!worldPoint) {
          return;
        }
        const startPoint = getSnappedRegionPoint({
          point: worldPoint,
          regions: displayedRegions,
          zoom: viewport.zoom,
        });
        const startPoints = [startPoint];
        regionDrawStateRef.current = { points: startPoints };
        setRegionDraftPoints(startPoints);
        setRegionDraftHoverPoint(startPoint);
        return;
      }
      if (mapTool === 'draw' && event.button === 0 && containerRef.current) {
        const worldPoint = toWorldPoint(event.clientX, event.clientY);
        if (!worldPoint) {
          return;
        }
        const startPoints = [worldPoint];
        drawingStateRef.current = {
          points: startPoints,
          viewport,
          startClientX: event.clientX,
          startClientY: event.clientY,
          hasMoved: false,
        };
        setDrawingDraftPoints(startPoints);
        return;
      }
      if (effectiveInteractionMode !== 'pan') {
        if (effectiveInteractionMode === 'select' && mapTool === 'select') {
          if (event.button !== 0) {
            return;
          }
          const rect = containerRef.current?.getBoundingClientRect();
          if (!rect) {
            return;
          }
          selectionDragStateRef.current = {
            startX: event.clientX - rect.left,
            startY: event.clientY - rect.top,
            hasMoved: false,
          };
      clearSelectionBox();
        }
        return;
      }
      if (event.button !== 0) {
        return;
      }
      panStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        startViewport: viewport,
        hasMoved: false,
      };
    },
    [
      buildLineDraftPath,
      clearSelectionBox,
      containerRef,
      displayedRegions,
      drawingStateRef,
      effectiveInteractionMode,
      editable,
      getSnappedRegionPoint,
      isAnyModalOpen,
      isMapElementTarget,
      mapShapeType,
      mapTool,
      panStateRef,
      regionDrawStateRef,
      selectionDragStateRef,
      setDrawingDraftPoints,
      setRegionDraftHoverPoint,
      setRegionDraftPoints,
      setShapeDraft,
      safeGetMapTargetNodeId,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      shapeDraftRef,
      startMeasureDraft,
      toWorldPoint,
      viewport,
    ],
  );

  const handlePinPointerDown = useCallback(
    (event, pinId) => {
      event.stopPropagation();
      if (startMeasureDraft(event)) {
        event.preventDefault();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool === 'line') {
        const nodeId = pinId ? `pin:${pinId}` : null;
        const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
        if (nodeId && fromRect) {
          const initial = {
            from: nodeId,
            viewport,
            toX: fromRect.cx,
            toY: fromRect.cy,
            path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
          };
          safeLineDraftRef.current = initial;
          safeSetLineDraft(initial);
        }
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const isUsingExistingSelection =
        Array.isArray(selectedPinIds) && selectedPinIds.includes(pinId);
      const activePinIds = isUsingExistingSelection
        ? [...new Set(selectedPinIds.filter(Boolean))]
        : [pinId];
      const activeShapeIds = isUsingExistingSelection
        ? [...new Set((selectedShapeIds || []).filter(Boolean))]
        : [];
      const activeWidgetIds = isUsingExistingSelection
        ? [...new Set((selectedWidgetIds || []).filter(Boolean))]
        : [];
      const activeRegionIds = isUsingExistingSelection
        ? [...new Set((selectedRegionIds || []).filter(Boolean))]
        : [];
      const activeDrawingIds = isUsingExistingSelection
        ? [...new Set((selectedDrawingIds || []).filter(Boolean))]
        : [];
      pinDragStateRef.current = {
        pinIds: activePinIds,
        shapeIds: activeShapeIds,
        widgetIds: activeWidgetIds,
        regionIds: activeRegionIds,
        drawingIds: activeDrawingIds,
        viewport,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (activePinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startShapesById: displayedShapes.reduce((acc, shape) => {
          if (activeShapeIds.includes(shape.id)) {
            acc[shape.id] = {
              x: Number(shape?.x) || 0,
              y: Number(shape?.y) || 0,
              points: Array.isArray(shape?.points)
                ? shape.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (activeRegionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (activeDrawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startLayoutsById: activeWidgetIds.reduce((acc, activeWidgetId) => {
          const activeWidget = mapWidgets.find(entry => getId(entry) === activeWidgetId);
          const payload = activeWidget ? getData(activeWidget) || {} : {};
          const baseLayout = widgetLayoutOverrides[activeWidgetId] || payload?.map || {};
          acc[activeWidgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!isUsingExistingSelection) {
        selectPinOnly(pinId);
      }
    },
    [
      buildLineDraftPath,
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      mapTool,
      mapWidgets,
      editable,
      pinDragStateRef,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      selectPinOnly,
      selectedDrawingIds,
      selectedPinIds,
      selectedRegionIds,
      selectedShapeIds,
      selectedWidgetIds,
      startMeasureDraft,
      viewport,
      widgetLayoutOverrides,
    ],
  );

  const handleShapePointerDown = useCallback(
    (event, shapeId) => {
      if (event.button !== 0) {
        return;
      }
      if (startMeasureDraft(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool === 'line') {
        const nodeId = shapeId ? `shape:${shapeId}` : null;
        const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
        if (nodeId && fromRect) {
          event.preventDefault();
          event.stopPropagation();
          const initial = {
            from: nodeId,
            viewport,
            toX: fromRect.cx,
            toY: fromRect.cy,
            path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
          };
          safeLineDraftRef.current = initial;
          safeSetLineDraft(initial);
        }
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const shape = displayedShapes.find(entry => entry?.id === shapeId);
      if (!shape) {
        return;
      }
      const isUsingExistingSelection =
        Array.isArray(selectedShapeIds) && selectedShapeIds.includes(shapeId);
      const activeShapeIds = isUsingExistingSelection
        ? [...new Set(selectedShapeIds.filter(Boolean))]
        : [shapeId];
      const activePinIds = isUsingExistingSelection
        ? [...new Set((selectedPinIds || []).filter(Boolean))]
        : [];
      const activeWidgetIds = isUsingExistingSelection
        ? [...new Set((selectedWidgetIds || []).filter(Boolean))]
        : [];
      const activeRegionIds = isUsingExistingSelection
        ? [...new Set((selectedRegionIds || []).filter(Boolean))]
        : [];
      const activeDrawingIds = isUsingExistingSelection
        ? [...new Set((selectedDrawingIds || []).filter(Boolean))]
        : [];
      event.preventDefault();
      event.stopPropagation();
      shapeDragStateRef.current = {
        shapeIds: activeShapeIds,
        pinIds: activePinIds,
        widgetIds: activeWidgetIds,
        regionIds: activeRegionIds,
        drawingIds: activeDrawingIds,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startShapesById: displayedShapes.reduce((acc, entry) => {
          if (activeShapeIds.includes(entry.id)) {
            acc[entry.id] = {
              x: Number(entry?.x) || 0,
              y: Number(entry?.y) || 0,
            };
          }
          return acc;
        }, {}),
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (activePinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (activeRegionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (activeDrawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startLayoutsById: activeWidgetIds.reduce((acc, activeWidgetId) => {
          const activeWidget = mapWidgets.find(entry => getId(entry) === activeWidgetId);
          const payload = activeWidget ? getData(activeWidget) || {} : {};
          const baseLayout = widgetLayoutOverrides[activeWidgetId] || payload?.map || {};
          acc[activeWidgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!isUsingExistingSelection) {
        selectShapeOnly(shapeId);
      }
    },
    [
      buildLineDraftPath,
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      editable,
      mapTool,
      mapWidgets,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      selectShapeOnly,
      selectedDrawingIds,
      selectedPinIds,
      selectedRegionIds,
      selectedShapeIds,
      selectedWidgetIds,
      shapeDragStateRef,
      startMeasureDraft,
      viewport,
      widgetLayoutOverrides,
    ],
  );

  const handleShapeResizePointerDown = useCallback(
    (event, shapeId) => {
      if (event.button !== 0) {
        return;
      }
      if (startMeasureDraft(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const shape = displayedShapes.find(entry => entry?.id === shapeId);
      if (!shape) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      shapeResizeStateRef.current = {
        shapeId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startW: Math.max(8, Number(shape?.w) || 0),
        startH: Math.max(8, Number(shape?.h) || 0),
        hasMoved: false,
      };
      if (!selectedShapeIds.includes(shapeId)) {
        selectShapeOnly(shapeId);
      }
    },
    [
      displayedShapes,
      editable,
      mapTool,
      selectShapeOnly,
      selectedShapeIds,
      shapeResizeStateRef,
      startMeasureDraft,
    ],
  );

  const handleRegionPointPointerDown = useCallback(
    (event, regionId, pointIndex) => {
      event.stopPropagation();
      if (startMeasureDraft(event)) {
        event.preventDefault();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      regionPointDragStateRef.current = {
        regionId,
        pointIndex,
        viewport,
        startClientX: event.clientX,
        startClientY: event.clientY,
        hasMoved: false,
      };
      selectRegionOnly(regionId);
    },
    [editable, mapTool, regionPointDragStateRef, selectRegionOnly, startMeasureDraft, viewport],
  );

  const handleRegionPointerDown = useCallback(
    (event, regionId) => {
      if (event.button !== 0) {
        return;
      }
      if (startMeasureDraft(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool === 'line') {
        const nodeId = regionId ? `region:${regionId}` : null;
        const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
        if (nodeId && fromRect) {
          event.preventDefault();
          event.stopPropagation();
          const initial = {
            from: nodeId,
            viewport,
            toX: fromRect.cx,
            toY: fromRect.cy,
            path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
          };
          safeLineDraftRef.current = initial;
          safeSetLineDraft(initial);
        }
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const activeRegionIds =
        Array.isArray(selectedRegionIds) && selectedRegionIds.includes(regionId)
          ? selectedRegionIds
          : [regionId];
      const isUsingExistingSelection =
        Array.isArray(selectedRegionIds) && selectedRegionIds.includes(regionId);
      const activePinIds = isUsingExistingSelection
        ? [...new Set((selectedPinIds || []).filter(Boolean))]
        : [];
      const activeShapeIds = isUsingExistingSelection
        ? [...new Set((selectedShapeIds || []).filter(Boolean))]
        : [];
      const activeWidgetIds = isUsingExistingSelection
        ? [...new Set((selectedWidgetIds || []).filter(Boolean))]
        : [];
      const activeDrawingIds = isUsingExistingSelection
        ? [...new Set((selectedDrawingIds || []).filter(Boolean))]
        : [];
      if (!activeRegionIds.length) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      regionDragStateRef.current = {
        regionIds: [...new Set(activeRegionIds.filter(Boolean))],
        pinIds: activePinIds,
        shapeIds: activeShapeIds,
        widgetIds: activeWidgetIds,
        drawingIds: activeDrawingIds,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (activeRegionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (activePinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startShapesById: displayedShapes.reduce((acc, shape) => {
          if (activeShapeIds.includes(shape.id)) {
            acc[shape.id] = {
              x: Number(shape?.x) || 0,
              y: Number(shape?.y) || 0,
              points: Array.isArray(shape?.points)
                ? shape.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (activeDrawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startLayoutsById: activeWidgetIds.reduce((acc, activeWidgetId) => {
          const activeWidget = mapWidgets.find(entry => getId(entry) === activeWidgetId);
          const payload = activeWidget ? getData(activeWidget) || {} : {};
          const baseLayout = widgetLayoutOverrides[activeWidgetId] || payload?.map || {};
          acc[activeWidgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!isUsingExistingSelection) {
        selectRegionOnly(regionId);
      }
    },
    [
      buildLineDraftPath,
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      editable,
      mapTool,
      mapWidgets,
      regionDragStateRef,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      selectRegionOnly,
      selectedDrawingIds,
      selectedPinIds,
      selectedRegionIds,
      selectedShapeIds,
      selectedWidgetIds,
      startMeasureDraft,
      viewport,
      widgetLayoutOverrides,
    ],
  );

  const handleDrawingPointerDown = useCallback(
    (event, drawingId) => {
      if (event.button !== 0) {
        return;
      }
      if (startMeasureDraft(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool === 'line') {
        const nodeId = drawingId ? `drawing:${drawingId}` : null;
        const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
        if (nodeId && fromRect) {
          event.preventDefault();
          event.stopPropagation();
          const initial = {
            from: nodeId,
            viewport,
            toX: fromRect.cx,
            toY: fromRect.cy,
            path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
          };
          safeLineDraftRef.current = initial;
          safeSetLineDraft(initial);
        }
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const activeDrawingIds =
        Array.isArray(selectedDrawingIds) && selectedDrawingIds.includes(drawingId)
          ? selectedDrawingIds
          : [drawingId];
      const isUsingExistingSelection =
        Array.isArray(selectedDrawingIds) && selectedDrawingIds.includes(drawingId);
      const activePinIds = isUsingExistingSelection
        ? [...new Set((selectedPinIds || []).filter(Boolean))]
        : [];
      const activeShapeIds = isUsingExistingSelection
        ? [...new Set((selectedShapeIds || []).filter(Boolean))]
        : [];
      const activeWidgetIds = isUsingExistingSelection
        ? [...new Set((selectedWidgetIds || []).filter(Boolean))]
        : [];
      const activeRegionIds = isUsingExistingSelection
        ? [...new Set((selectedRegionIds || []).filter(Boolean))]
        : [];
      if (!activeDrawingIds.length) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      drawingDragStateRef.current = {
        drawingIds: [...new Set(activeDrawingIds.filter(Boolean))],
        pinIds: activePinIds,
        shapeIds: activeShapeIds,
        widgetIds: activeWidgetIds,
        regionIds: activeRegionIds,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (activeDrawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (activePinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startShapesById: displayedShapes.reduce((acc, shape) => {
          if (activeShapeIds.includes(shape.id)) {
            acc[shape.id] = {
              x: Number(shape?.x) || 0,
              y: Number(shape?.y) || 0,
              points: Array.isArray(shape?.points)
                ? shape.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (activeRegionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startLayoutsById: activeWidgetIds.reduce((acc, activeWidgetId) => {
          const activeWidget = mapWidgets.find(entry => getId(entry) === activeWidgetId);
          const payload = activeWidget ? getData(activeWidget) || {} : {};
          const baseLayout = widgetLayoutOverrides[activeWidgetId] || payload?.map || {};
          acc[activeWidgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!isUsingExistingSelection) {
        selectDrawingOnly(drawingId);
      }
    },
    [
      buildLineDraftPath,
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      drawingDragStateRef,
      editable,
      mapTool,
      mapWidgets,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      selectDrawingOnly,
      selectedDrawingIds,
      selectedPinIds,
      selectedRegionIds,
      selectedShapeIds,
      selectedWidgetIds,
      startMeasureDraft,
      viewport,
      widgetLayoutOverrides,
    ],
  );

  const handleWidgetDragStart = useCallback(
    (event, widgetId) => {
      if (startMeasureDraft(event)) {
        return;
      }
      if (!editable) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (mapTool === 'line') {
        const nodeId = widgetId ? `widget:${widgetId}` : null;
        const fromRect = nodeId ? safeMapNodeRectById?.get(nodeId) : null;
        if (nodeId && fromRect) {
          const initial = {
            from: nodeId,
            viewport,
            toX: fromRect.cx,
            toY: fromRect.cy,
            path: buildLineDraftPath(nodeId, fromRect.cx, fromRect.cy),
          };
          safeLineDraftRef.current = initial;
          safeSetLineDraft(initial);
        }
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const activeWidgetIds =
        Array.isArray(selectedWidgetIds) && selectedWidgetIds.includes(widgetId)
          ? [...new Set(selectedWidgetIds.filter(Boolean))]
          : [widgetId];
      const isUsingExistingSelection =
        Array.isArray(selectedWidgetIds) && selectedWidgetIds.includes(widgetId);
      const activePinIds = isUsingExistingSelection
        ? [...new Set((selectedPinIds || []).filter(Boolean))]
        : [];
      const activeShapeIds = isUsingExistingSelection
        ? [...new Set((selectedShapeIds || []).filter(Boolean))]
        : [];
      const activeRegionIds = isUsingExistingSelection
        ? [...new Set((selectedRegionIds || []).filter(Boolean))]
        : [];
      const activeDrawingIds = isUsingExistingSelection
        ? [...new Set((selectedDrawingIds || []).filter(Boolean))]
        : [];
      widgetDragStateRef.current = {
        widgetIds: activeWidgetIds,
        pinIds: activePinIds,
        shapeIds: activeShapeIds,
        regionIds: activeRegionIds,
        drawingIds: activeDrawingIds,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startLayoutsById: activeWidgetIds.reduce((acc, activeWidgetId) => {
          const activeWidget = mapWidgets.find(entry => getId(entry) === activeWidgetId);
          const payload = activeWidget ? getData(activeWidget) || {} : {};
          const baseLayout = widgetLayoutOverrides[activeWidgetId] || payload?.map || {};
          acc[activeWidgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (activePinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startShapesById: displayedShapes.reduce((acc, shape) => {
          if (activeShapeIds.includes(shape.id)) {
            acc[shape.id] = {
              x: Number(shape?.x) || 0,
              y: Number(shape?.y) || 0,
              points: Array.isArray(shape?.points)
                ? shape.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (activeRegionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (activeDrawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!isUsingExistingSelection) {
        selectWidgetOnly(widgetId);
      }
    },
    [
      mapWidgets,
      buildLineDraftPath,
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      editable,
      mapTool,
      selectWidgetOnly,
      selectedDrawingIds,
      selectedPinIds,
      selectedRegionIds,
      selectedShapeIds,
      selectedWidgetIds,
      safeLineDraftRef,
      safeMapNodeRectById,
      safeSetLineDraft,
      startMeasureDraft,
      viewport,
      widgetDragStateRef,
      widgetLayoutOverrides,
    ],
  );

  const handleWidgetResizeStart = useCallback(
    (event, widgetId) => {
      if (startMeasureDraft(event)) {
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const widget = mapWidgets.find(entry => getId(entry) === widgetId);
      const payload = widget ? getData(widget) || {} : {};
      const baseLayout = widgetLayoutOverrides[widgetId] || payload?.map || {};
      widgetResizeStateRef.current = {
        widgetId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startLayout: {
          x: Number(baseLayout?.x) || 0,
          y: Number(baseLayout?.y) || 0,
          w: Math.max(80, Number(baseLayout?.w) || 0),
          h: Math.max(24, Number(baseLayout?.h) || 0),
        },
        hasMoved: false,
      };
      if (!selectedWidgetIds.includes(widgetId)) {
        selectWidgetOnly(widgetId);
      }
    },
    [
      mapWidgets,
      editable,
      mapTool,
      selectWidgetOnly,
      selectedWidgetIds,
      startMeasureDraft,
      widgetLayoutOverrides,
      widgetResizeStateRef,
    ],
  );

  const handleGroupPointerDown = useCallback(
    (event, groupId) => {
      if (event.button !== 0) {
        return;
      }
      if (startMeasureDraft(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!editable) {
        return;
      }
      if (mapTool !== 'select') {
        return;
      }
      const group = displayedGroups.find(entry => entry?.id === groupId);
      if (!group) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const childIds = Array.isArray(group?.childIds) ? group.childIds : [];
      const widgetIds = childIds
        .filter(nodeId => String(nodeId).startsWith('widget:'))
        .map(nodeId => String(nodeId).slice(7));
      const shapeIds = childIds
        .filter(nodeId => String(nodeId).startsWith('shape:'))
        .map(nodeId => String(nodeId).slice(6));
      const pinIds = childIds
        .filter(nodeId => String(nodeId).startsWith('pin:'))
        .map(nodeId => String(nodeId).slice(4));
      const regionIds = childIds
        .filter(nodeId => String(nodeId).startsWith('region:'))
        .map(nodeId => String(nodeId).slice(7));
      const drawingIds = childIds
        .filter(nodeId => String(nodeId).startsWith('drawing:'))
        .map(nodeId => String(nodeId).slice(8));
      groupDragStateRef.current = {
        groupId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startGroup: {
          x: Number(group?.x) || 0,
          y: Number(group?.y) || 0,
        },
        widgetIds,
        shapeIds,
        pinIds,
        regionIds,
        drawingIds,
        startLayoutsById: widgetIds.reduce((acc, widgetId) => {
          const widget = mapWidgets.find(entry => getId(entry) === widgetId);
          const payload = widget ? getData(widget) || {} : {};
          const baseLayout = widgetLayoutOverrides[widgetId] || payload?.map || {};
          acc[widgetId] = {
            x: Number(baseLayout?.x) || 0,
            y: Number(baseLayout?.y) || 0,
            w: Math.max(80, Number(baseLayout?.w) || 0),
            h: Math.max(24, Number(baseLayout?.h) || 0),
          };
          return acc;
        }, {}),
        startPinsById: displayedPins.reduce((acc, pin) => {
          if (pinIds.includes(pin.id)) {
            acc[pin.id] = { x: Number(pin?.x) || 0, y: Number(pin?.y) || 0 };
          }
          return acc;
        }, {}),
        startShapesById: displayedShapes.reduce((acc, shape) => {
          if (shapeIds.includes(shape.id)) {
            acc[shape.id] = {
              x: Number(shape?.x) || 0,
              y: Number(shape?.y) || 0,
              points: Array.isArray(shape?.points)
                ? shape.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : null,
            };
          }
          return acc;
        }, {}),
        startRegionsById: displayedRegions.reduce((acc, region) => {
          if (regionIds.includes(region.id)) {
            acc[region.id] = {
              points: Array.isArray(region?.points)
                ? region.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        startDrawingsById: displayedDrawings.reduce((acc, drawing) => {
          if (drawingIds.includes(drawing.id)) {
            acc[drawing.id] = {
              points: Array.isArray(drawing?.points)
                ? drawing.points.map(point => ({
                    x: Number(point?.x) || 0,
                    y: Number(point?.y) || 0,
                  }))
                : [],
            };
          }
          return acc;
        }, {}),
        hasMoved: false,
      };
      if (!selectedGroupIds.includes(groupId)) {
        selectGroupOnly(groupId);
      }
    },
    [
      displayedDrawings,
      displayedGroups,
      displayedPins,
      displayedRegions,
      displayedShapes,
      editable,
      groupDragStateRef,
      mapTool,
      mapWidgets,
      selectGroupOnly,
      selectedGroupIds,
      startMeasureDraft,
      widgetLayoutOverrides,
    ],
  );

  const handleCanvasClick = useCallback(
    async event => {
      if (isAnyModalOpen) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        (
          target.closest('[data-map-widget-root="true"]') ||
          target.closest('[data-map-shape-root="true"]') ||
          target.closest('[data-map-region-root="true"]') ||
          target.closest('[data-map-drawing-root="true"]')
        )
      ) {
        return;
      }
      if (didRegionDrawRef.current) {
        didRegionDrawRef.current = false;
        return;
      }
      if (didDrawingRef.current) {
        didDrawingRef.current = false;
        return;
      }
      if (didRegionPointDragRef.current) {
        didRegionPointDragRef.current = false;
        return;
      }
      if (didSelectionDragRef.current) {
        didSelectionDragRef.current = false;
        return;
      }
      if (mapTool === 'region') {
        return;
      }
      if (mapTool === 'shape') {
        return;
      }
      if (mapTool === 'measure') {
        return;
      }

      if (mapTool !== 'pin') {
        clearSelection();
        return;
      }
      if (!editable) {
        return;
      }
      if (!containerRef.current) {
        return;
      }
      if (didPanRef.current) {
        didPanRef.current = false;
        return;
      }
      if (target instanceof HTMLElement) {
        if (target.closest('button, input, textarea, label')) {
          return;
        }
      }
      const worldPoint = toWorldPoint(event.clientX, event.clientY);
      if (!worldPoint) {
        return;
      }
      const nextPin = {
        id:
          typeof crypto !== 'undefined' &&
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `pin_${Date.now()}`,
        x: worldPoint.x,
        y: worldPoint.y,
        label: '',
        color: 'secondary',
        icon: 'map-pin',
        iconSlug: null,
        size: 'md',
        linkedItemId: null,
        note: '',
        visible: true,
        locked: false,
      };
      const nextPins = [...mapState.pins, nextPin];
      await persistMapField('pins', nextPins);
      selectPinOnly(nextPin.id);
    },
    [
      clearSelection,
      containerRef,
      didDrawingRef,
      didPanRef,
      didRegionDrawRef,
      didRegionPointDragRef,
      didSelectionDragRef,
      editable,
      isAnyModalOpen,
      mapState.pins,
      mapTool,
      persistMapField,
      selectPinOnly,
      toWorldPoint,
    ],
  );

  return {
    handleCanvasPointerDown,
    handleCanvasClick,
    handlePinPointerDown,
    handleShapePointerDown,
    handleShapeResizePointerDown,
    handleRegionPointerDown,
    handleRegionPointPointerDown,
    handleDrawingPointerDown,
    handleWidgetDragStart,
    handleWidgetResizeStart,
    handleGroupPointerDown,
  };
};
